'use client';
import React, { useState } from 'react';
import { RotateCcw, Download } from 'lucide-react';
import ReportOverview from './ReportOverview';
import ReportQuestionAnalysis from './ReportQuestionAnalysis';
import ReportTimeAnalysis from './ReportTimeAnalysis';
import ReportErrorBreakdown from './ReportErrorBreakdown';
import ReportRevisionPlan from './ReportRevisionPlan';
import { MOCK_RESULTS } from './MockExamScreen';

const TABS = [
  { id: 'tab-overview', label: 'Overview' },
  { id: 'tab-questions', label: 'Question Analysis' },
  { id: 'tab-time', label: 'Time Analysis' },
  { id: 'tab-errors', label: 'Error Breakdown' },
  { id: 'tab-revision', label: 'Revision Plan' },
];

interface Props {
  results: typeof MOCK_RESULTS;
  onRetake: () => void;
}

export default function PerformanceReport({ results, onRetake }: Props) {
  const [activeTab, setActiveTab] = useState('tab-overview');

  const band = results.rawScore >= 38 ? '8.5' : results.rawScore >= 35 ? '8.0' : results.rawScore >= 32 ? '7.5' : results.rawScore >= 28 ? '7.0' : '6.5';
  const gapToTarget = Math.max(0, 35 - results.rawScore);

  return (
    <div className="min-h-screen bg-background">
      {/* Report header */}
      <div className="bg-card border-b border-border px-6 lg:px-8 xl:px-10 py-5">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-semibold text-foreground">Mock Exam 1 — Performance Report</h1>
                <span className="text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded-md">31 Jul 2026</span>
              </div>
              <p className="text-sm text-muted-foreground">Academic Reading · 40 questions · 59:07 completed</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button className="btn-outline py-2 px-4 text-xs flex items-center gap-1.5">
                <Download size={13} />
                Export PDF
              </button>
              <button onClick={onRetake} className="btn-primary py-2 px-4 text-xs flex items-center gap-1.5">
                <RotateCcw size={13} />
                Retake Mock
              </button>
            </div>
          </div>

          {/* Hero score strip */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { id: 'rs-raw', label: 'Raw Score', value: `${results.rawScore}/40`, highlight: false },
              { id: 'rs-band', label: 'Band Estimate', value: band, highlight: true },
              { id: 'rs-gap', label: 'Gap to Band 8.0', value: gapToTarget === 0 ? '✓ Achieved' : `${gapToTarget} marks`, highlight: false, alert: gapToTarget > 0 },
              { id: 'rs-acc', label: 'Overall Accuracy', value: `${Math.round((results.rawScore / 40) * 100)}%`, highlight: false },
              { id: 'rs-time', label: 'Avg Time/Q', value: `${Math.round(results.timeTaken / 40)}s`, highlight: false, alert: Math.round(results.timeTaken / 40) > 90 },
            ].map((stat) => (
              <div
                key={stat.id}
                className={`p-3.5 rounded-xl border-2 text-center ${
                  stat.highlight
                    ? 'border-primary bg-secondary'
                    : stat.alert
                    ? 'border-warning/40 bg-warning-bg' :'border-border bg-muted/40'
                }`}
              >
                <p className="metric-label mb-1">{stat.label}</p>
                <p className={`text-xl font-bold tabular-nums ${stat.highlight ? 'text-primary' : stat.alert ? 'text-warning' : 'text-foreground'}`}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {/* Gap callout */}
          {gapToTarget > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground text-sm font-bold">{gapToTarget}</span>
              </div>
              <p className="text-sm text-foreground">
                <span className="font-semibold">You need {gapToTarget} more correct answer{gapToTarget !== 1 ? 's' : ''} to reach Band 8.0.</span>
                {' '}Your Revision Plan (last tab) identifies exactly which question types to target.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border-b border-border px-6 lg:px-8 xl:px-10">
        <div className="max-w-screen-2xl mx-auto flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6">
        {activeTab === 'tab-overview' && <ReportOverview results={results} band={band} gapToTarget={gapToTarget} />}
        {activeTab === 'tab-questions' && <ReportQuestionAnalysis results={results} />}
        {activeTab === 'tab-time' && <ReportTimeAnalysis results={results} />}
        {activeTab === 'tab-errors' && <ReportErrorBreakdown results={results} />}
        {activeTab === 'tab-revision' && <ReportRevisionPlan results={results} gapToTarget={gapToTarget} />}
      </div>
    </div>
  );
}